import React from 'react';
import { Box, Container, Typography, Paper, Link, List, ListItem, ListItemText } from '@mui/material';

function PrivacyPolicy() {
    const Section = ({ title, children }) => (
        <Paper className="hk-policy-section" id={title.toLowerCase().replace(/[^a-z0-9]+/g, '-')} elevation={0} sx={{ p: 4, mb: 4, border: '1px solid', borderColor: 'divider', borderRadius: 4 }}>
            <Typography variant="h5" component="h2" gutterBottom sx={{ color: 'text.primary', fontWeight: 600 }}>
                {title}
            </Typography>
            <Box sx={{ color: 'text.secondary', lineHeight: 1.7 }}>
                {children}
            </Box>
        </Paper>
    );

    return (
        <Box className="hk-policy">
            <Container maxWidth="lg">
                <Box className="hk-policy-heading"><a className="hk-breadcrumb" href="/">Home / Privacy</a><span className="hk-eyebrow">YOUR INFORMATION, EXPLAINED</span>
                    <Typography variant="h3" component="h1" gutterBottom sx={{ fontWeight: 800, color: 'primary.main' }}>
                        Privacy Policy
                    </Typography>
                    <Typography variant="subtitle1" color="text.secondary">
                        Effective date: January 21, 2024
                    </Typography>
                </Box>

<div className="hk-policy-layout"><aside className="hk-policy-toc"><span className="hk-eyebrow">ON THIS PAGE</span><a href="#introduction">Introduction</a><a href="#information-collection-and-use">Information Collection and Use</a><a href="#permissions">Permissions</a><a href="#log-data">Log Data</a><a href="#cookies">Cookies</a><a href="#service-providers">Service Providers</a><a href="#security">Security</a><a href="#links-to-other-sites">Links to Other Sites</a><a href="#children-s-privacy">Children’s Privacy</a><a href="#changes-to-this-privacy-policy">Changes to This Privacy Policy</a><a href="#contact-us">Contact Us</a></aside><div>                <Section title="Introduction">
                    <Typography paragraph>
                        Fimly Innovations has built the Hisab Kitab app as a Free app. This SERVICE is provided by Fimly Innovations at no cost and is intended for use as is.
                    </Typography>
                    <Typography paragraph>
                        This page is used to inform visitors regarding my policies with the collection, use, and disclosure
                        of Personal Information if anyone decided to use my Service.
                    </Typography>
                    <Typography paragraph>
                        If you choose to use my Service, then you agree to the collection and use of information in relation
                        to this policy. The Personal Information that I collect is used for providing and improving the
                        Service. I will not use or share your information with anyone except as described in this Privacy Policy.
                    </Typography>
                    <Typography>
                        The terms used in this Privacy Policy have the same meanings as in our Terms and Conditions, which
                        are accessible at Hisab Kitab unless otherwise defined in this Privacy Policy.
                    </Typography>
                </Section>

                <Section title="Information Collection and Use">
                    <Typography gutterBottom sx={{ fontWeight: 600 }}>Personal Information Collected</Typography>
                    <List disablePadding>
                        <ListItem disableGutters sx={{ display: 'block' }}>
                            <Typography variant="subtitle1" fontWeight={600}>Phone Number</Typography>
                            <Typography variant="body2">We use your phone number for account verification. Also we use phone number to allow your friend and family members to add you to expense groups.</Typography>
                        </ListItem>
                        <ListItem disableGutters sx={{ display: 'block', mt: 2 }}>
                            <Typography variant="subtitle1" fontWeight={600}>Email Address</Typography>
                            <Typography variant="body2">Your email address is used for account registration, password recovery, and communication purposes.</Typography>
                        </ListItem>
                        <ListItem disableGutters sx={{ display: 'block', mt: 2 }}>
                            <Typography variant="subtitle1" fontWeight={600}>Images/Photos</Typography>
                            <Typography variant="body2">Any images or photos uploaded by you are used to personalize your account and enhance the app's features. For example you can upload photo for avatar or a group icon.</Typography>
                        </ListItem>
                    </List>
                    <Typography paragraph sx={{ mt: 2 }}>
                        These information will be stored on server for better synchronization across devices.
                    </Typography>
                    <Typography paragraph>
                        The app does use third-party services that may collect information used to identify you.
                        Link to the privacy policy of third-party service providers used by the app:
                    </Typography>
                    <List sx={{ listStyleType: 'disc', pl: 4 }}>
                        <ListItem sx={{ display: 'list-item', p: 0 }}>
                            <Link href="https://www.google.com/policies/privacy/" target="_blank" rel="noopener noreferrer">Google Play Services</Link>
                        </ListItem>
                    </List>
                </Section>

                <Section title="Permissions">
                    <Typography gutterBottom sx={{ fontWeight: 600 }}>Permissions Requested</Typography>
                    <List disablePadding>
                        {[
                            { title: 'Internet Access (Mandatory)', desc: 'Allows the app to access internet for data synchronization and online services.' },
                            { title: 'Access Network State (Mandatory)', desc: 'Enables the app to access information about network connections to determine whether you are connected to the internet.' },
                            { title: 'Contacts Access (Optional)', desc: 'Allows the app to import contacts for easy sharing and collaboration. This will be used to invite contacts to group for expense sharing.' },
                            { title: 'Notification Access (Optional)', desc: 'To provide app feature like notifying account activity and any feature update.' },
                            { title: 'In-app Billing (Optional)', desc: 'Enables in-app purchases, allowing you to buy additional features or content within the app.' }
                        ].map((item, index) => (
                            <ListItem key={index} disableGutters sx={{ display: 'block', mt: index === 0 ? 0 : 2 }}>
                                <Typography variant="subtitle1" fontWeight={600}>{item.title}</Typography>
                                <Typography variant="body2">{item.desc}</Typography>
                            </ListItem>
                        ))}
                    </List>
                </Section>

                <Section title="Log Data">
                    <Typography>
                        I want to inform you that whenever you use my Service, in a case of an error in the app, I collect
                        data and information (through third-party products) on your phone called Log Data. This Log Data may
                        include information such as your device Internet Protocol (“IP”) address, device name, operating system
                        version, the configuration of the app when utilizing my Service, the time and date of your use of the
                        Service, and other statistics.
                    </Typography>
                </Section>

                <Section title="Cookies">
                    <Typography paragraph>
                        Cookies are files with a small amount of data that are commonly used as anonymous unique identifiers.
                        These are sent to your browser from the websites that you visit and are stored on your device's
                        internal memory.
                    </Typography>
                    <Typography>
                        This Service does not use these “cookies” explicitly. However, the app may use third-party code and
                        libraries that use “cookies” to collect information and improve their services. You have the option to
                        either accept or refuse these cookies and know when a cookie is being sent to your device. If you choose
                        to refuse our cookies, you may not be able to use some portions of this Service.
                    </Typography>
                </Section>

                <Section title="Service Providers">
                    <Typography paragraph>
                        I may employ third-party companies and individuals due to the following reasons:
                    </Typography>
                    <List sx={{ listStyleType: 'disc', pl: 4 }}>
                        {['To facilitate our Service;', 'To provide the Service on our behalf;', 'To perform Service-related services; or', 'To assist us in analyzing how our Service is used.'].map((text, i) => (
                            <ListItem key={i} sx={{ display: 'list-item', p: 0 }}>
                                <ListItemText primary={text} />
                            </ListItem>
                        ))}
                    </List>
                    <Typography sx={{ mt: 2 }}>
                        I want to inform users of this Service that these third parties have access to their Personal
                        Information. The reason is to perform the tasks assigned to them on our behalf. However, they are
                        obligated not to disclose or use the information for any other purpose.
                    </Typography>
                </Section>

                <Section title="Security">
                    <Typography>
                        We value your trust in providing us your Personal Information; thus, we strive to use commercially
                        acceptable means of protecting it. However, remember that no method of transmission over the
                        internet or method of electronic storage is 100% secure and reliable, and we cannot guarantee its absolute
                        security.
                    </Typography>
                </Section>

                <Section title="Links to Other Sites">
                    <Typography>
                        This Service may contain links to other sites. If you click on a third-party link, you will be
                        directed to that site. Note that these external sites are not operated by me. Therefore, I strongly advise
                        you to review the Privacy Policy of these websites. I have no control over and assume no responsibility for
                        the content, privacy policies, or practices of any third-party sites or services.
                    </Typography>
                </Section>

                <Section title="Children’s Privacy">
                    <Typography>
                        These Services do not address anyone under the age of 13. I do not knowingly collect personally
                        identifiable information from children under 13 years of age. In the case I discover that a child
                        under 13 has provided me with personal information, I immediately delete this from our servers.
                        If you are a parent or guardian, and you are aware that your child has provided us with personal
                        information, please contact me so that I will be able to take the necessary actions.
                    </Typography>
                </Section>

                <Section title="Changes to This Privacy Policy">
                    <Typography>
                        I may update our Privacy Policy from time to time. Thus, you are advised to review this page
                        periodically for any changes. I will notify you of any changes by posting the new Privacy Policy on
                        this page.
                    </Typography>
                </Section>

                <Section title="Contact Us">
                    <Typography>
                        If you have any questions or suggestions about our Privacy Policy, contact Fimly Innovations at{' '}
                        <Link href="mailto:support@hisabkitab-app.com">support@hisabkitab-app.com</Link>.
                    </Typography>
                </Section>
</div></div>            </Container>
        </Box>
    );
}

export default PrivacyPolicy;